import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { FolderIcon } from 'base/components/Icon';
import { textBold } from 'theme/theme';
import ExpandToggle from 'base/components/ExpandToggle';
import Flex from 'base/components/Flex';
import FolderPermissions from '../../../containers/FolderPermissions';

function isDescendant(older, younger) {
  return (
    !!older.children
      && typeof older.children !== 'function'
      && older.children.some((child) => child === younger || isDescendant(child, younger))
  );
}


const RowWrapper = styled(Flex)`
  position: relative;
  padding: 0;
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  align-items: center;
`;

const rowLandingPad = css`
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
  * {
    opacity: 0 !important;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: -1;
    width: 100%;
  }
`;

const Row = styled(Flex)`
  height: 100%;
  white-space: nowrap;
  padding-left: 20px;
  
  ${(props) => props.node && props.node.type && props.node.type === 'space' && css`
    ${textBold};
  `};

  ${(props) => (props.isLandingPadActive && rowLandingPad)}
  
  ${(props) => (props.isLandingPadActive && !props.canDrop && css`
    &::before {
      background-color: #e6a8ad;
    }
  `)}
`;

const RowItem = styled(Flex)`
  align-items: center;
`;

const RowContent = styled(RowItem)`
  position: relative;
  height: 100%;
  justify-content: flex-end;

  ${(props) => props.isSearchMatch && css`
    outline: solid 1px #0080ff;
  `}
  ${(props) => props.isSearchFocus && css`
    outline: solid 1px #fc6421;
  `}
`;

const Label = styled.span`
  padding-left: 4px;
`;


// eslint-disable-next-line react/prefer-stateless-function
class PermissionThemeNodeContentRenderer extends Component {
  render() {
    const {
      scaffoldBlockPxWidth,
      toggleChildrenVisibility,
      connectDragPreview,
      connectDragSource,
      isDragging,
      canDrop,
      canDrag,
      node,
      title,
      name,
      draggedNode,
      path,
      treeIndex,
      isSearchMatch,
      isSearchFocus,
      icons,
      buttons,
      className,
      style,
      didDrop,
      swapFrom,
      swapLength,
      swapDepth,
      treeId, // Not needed, but preserved for other renderers
      isOver, // Not needed, but preserved for other renderers
      parentNode, // Needed for dndManager
      readOnly,
      ...otherProps
    } = this.props;

    const isDraggedDescendant = draggedNode && isDescendant(draggedNode, node);
    const isLandingPadActive = !didDrop && isDragging;

    const toggleChildren = () => toggleChildrenVisibility({ node, path, treeIndex });

    return (
      <Flex {...otherProps}>

        <RowWrapper
          canDrag={canDrag}
        >
          {toggleChildrenVisibility
          && node.children
          && (node.children.length > 0 || typeof node.children === 'function')
          && (
          <span>
            <ExpandToggle
              isOpen={node.expanded}
              onClick={toggleChildren}
              style={{
                position: 'absolute',
                left: 0,
                top: '50%',
                marginTop: '-10px',
              }}
            />

            {node.expanded
                && !isDragging
                && (<span style={{ width: scaffoldBlockPxWidth }} />
                )}
          </span>
          )}

          <Row
            node={node}
            canDrop={canDrop}
            isLandingPadActive={isLandingPadActive}
            style={{
              opacity: isDraggedDescendant ? 0.5 : 1,
              // paddingLeft: scaffoldBlockPxWidth,
            }}
          >
            <RowContent
              isSearchMatch={isSearchMatch}
              isSearchFocus={isSearchFocus}
              canDrag={canDrag}
              ref={(instance) => canDrag && connectDragSource(instance, { dropEffect: 'copy' })}
            >

              <Flex alignSelf="flex-start center" flex="1 1 auto">
                {!node.type && <FolderIcon onClick={toggleChildren} />}
                <Label onClick={toggleChildren}>{node.name}</Label>
              </Flex>

              <FolderPermissions
                id={node.id}
                type={node.type}
                isExpanded={node.expanded}
                readOnly={readOnly}
              />

            </RowContent>
          </Row>
        </RowWrapper>
      </Flex>
    );
  }
}

PermissionThemeNodeContentRenderer.defaultProps = {
  buttons: [],
  canDrag: false,
  canDrop: false,
  className: '',
  draggedNode: null,
  icons: [],
  isSearchFocus: false,
  isSearchMatch: false,
  parentNode: null,
  style: {},
  subtitle: null,
  swapDepth: null,
  swapFrom: null,
  swapLength: null,
  title: null,
  name: null,
  toggleChildrenVisibility: null,
  readOnly: false,
};

PermissionThemeNodeContentRenderer.propTypes = {
  buttons: PropTypes.arrayOf(PropTypes.node),
  canDrag: PropTypes.bool,
  className: PropTypes.string,
  icons: PropTypes.arrayOf(PropTypes.node),
  isSearchFocus: PropTypes.bool,
  isSearchMatch: PropTypes.bool,
  node: PropTypes.shape({}).isRequired,
  path: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  scaffoldBlockPxWidth: PropTypes.number.isRequired,
  style: PropTypes.shape({}),
  subtitle: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  swapDepth: PropTypes.number,
  swapFrom: PropTypes.number,
  swapLength: PropTypes.number,
  title: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  name: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  toggleChildrenVisibility: PropTypes.func,
  treeIndex: PropTypes.number.isRequired,
  treeId: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,

  // Drag and drop API functions
  // Drag source
  connectDragPreview: PropTypes.func.isRequired,
  connectDragSource: PropTypes.func.isRequired,
  didDrop: PropTypes.bool.isRequired,
  draggedNode: PropTypes.shape({}),
  isDragging: PropTypes.bool.isRequired,
  parentNode: PropTypes.shape({}), // Needed for dndManager
  // Drop target
  canDrop: PropTypes.bool,
  isOver: PropTypes.bool.isRequired,
};

export default PermissionThemeNodeContentRenderer;
